import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaFilePdf } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CatalogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CatalogModal = ({ isOpen, onClose }: CatalogModalProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Enviar email notificando sobre acesso ao catálogo
      await fetch('/api/catalog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      // Abrir PDF em nova aba
      window.open('https://drive.google.com/file/d/1fPYcf6WGOqRuqL0Qs_lkCSmGLc6-QN6J/view?usp=sharing', '_blank');

      // Fechar modal e limpar email
      onClose();
      setEmail('');
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
      // Mesmo com erro, abrir o catálogo
      window.open('https://drive.google.com/file/d/1fPYcf6WGOqRuqL0Qs_lkCSmGLc6-QN6J/view?usp=sharing', '_blank');
      onClose();
      setEmail('');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          />

          {/* Modal Container */}
          <div
            className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Fechar modal"
              >
                <FaTimes size={24} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="bg-gradient-to-br from-primary-400 to-primary-600 text-white w-20 h-20 rounded-full flex items-center justify-center shadow-lg">
                  <FaFilePdf size={36} />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-dark text-center mb-2">
                Catálogo Digital
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Informe seu e-mail para acessar nosso catálogo completo
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="catalog-email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="catalog-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaFilePdf size={20} />
                  {isSubmitting ? 'Abrindo...' : 'Acessar Catálogo'}
                </button>
              </form>

              {/* Footer Note */}
              <p className="text-xs text-gray-500 text-center mt-4">
                O catálogo será aberto em uma nova aba
              </p>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default CatalogModal;

